import { useCallback, useEffect, useMemo, useState } from "react";
import { http, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import { Button } from "@/components/ui/button";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { ReceiptButton } from "@/components/ReceiptButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    ForkKnife,
    Car,
    Bed,
    Package,
    DotsThreeCircle,
    Receipt,
    DownloadSimple,
    CheckCircle,
    Circle,
    GoogleLogo,
    CloudCheck,
} from "@phosphor-icons/react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const CATEGORIES = [
    { v: "dieta", l: "Dieta / manutención", Icon: ForkKnife },
    { v: "desplazamiento", l: "Desplazamiento", Icon: Car },
    { v: "alojamiento", l: "Alojamiento", Icon: Bed },
    { v: "material", l: "Material", Icon: Package },
    { v: "otro", l: "Otro", Icon: DotsThreeCircle },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.v, c]));

const eur = (n) =>
    new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
    }).format(Number(n) || 0);

function yearRange() {
    const now = new Date();
    return {
        from: `${now.getFullYear()}-01-01`,
        to: `${now.getFullYear()}-12-31`,
    };
}

export default function AdminExpenses() {
    const range = yearRange();
    const [dateFrom, setDateFrom] = useState(range.from);
    const [dateTo, setDateTo] = useState(range.to);
    const [projectFilter, setProjectFilter] = useState("all");
    const [userFilter, setUserFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        http.get("/projects").then((r) => setProjects(r.data));
        http.get("/users").then((r) => setUsers(r.data));
    }, []);

    const [drive, setDrive] = useState({ connected: false });
    const loadDrive = useCallback(() => {
        http.get("/drive/status")
            .then((r) => setDrive(r.data))
            .catch(() => {});
    }, []);
    useEffect(() => {
        loadDrive();
        const p = new URLSearchParams(window.location.search);
        if (p.get("drive_connected") === "1") {
            toast.success("Google Drive conectado correctamente");
            window.history.replaceState({}, "", window.location.pathname);
        } else if (p.get("drive_error") === "1") {
            const msg = p.get("msg");
            toast.error(
                msg
                    ? `No se pudo conectar Google Drive: ${decodeURIComponent(msg)}`
                    : "No se pudo conectar Google Drive",
                { duration: 12000 },
            );
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, [loadDrive]);

    const connectDrive = async () => {
        try {
            const r = await http.get("/drive/connect", {
                params: { origin: window.location.origin },
            });
            window.location.href = r.data.authorization_url;
        } catch {
            toast.error("No se pudo iniciar la conexión con Drive");
        }
    };

    const load = useCallback(async () => {
        setBusy(true);
        const params = { date_from: dateFrom, date_to: dateTo, all_users: true };
        if (projectFilter !== "all") params.project_id = projectFilter;
        try {
            const ex = await http.get("/expenses", { params });
            setItems(ex.data);
        } finally {
            setBusy(false);
        }
    }, [dateFrom, dateTo, projectFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        return items.filter((e) => {
            if (userFilter !== "all" && e.user_id !== userFilter) return false;
            if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
            return true;
        });
    }, [items, userFilter, categoryFilter]);

    const togglePaid = async (exp) => {
        const next = !exp.paid;
        try {
            await http.patch(`/expenses/${exp.expense_id}/paid`, { paid: next });
            setItems((prev) =>
                prev.map((it) =>
                    it.expense_id === exp.expense_id ? { ...it, paid: next } : it,
                ),
            );
            toast.success(next ? "Gasto marcado como pagado" : "Gasto marcado como pendiente");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const summary = useMemo(() => {
        const byCat = {};
        let total = 0;
        let km = 0;
        for (const e of filtered) {
            const amt = Number(e.amount) || 0;
            total += amt;
            byCat[e.category] = (byCat[e.category] || 0) + amt;
            if (e.category === "desplazamiento" && e.km) km += Number(e.km) || 0;
        }
        return { total, byCat, km, count: filtered.length };
    }, [filtered]);

    const csvUrl = () => {
        const params = new URLSearchParams();
        params.set("date_from", dateFrom);
        params.set("date_to", dateTo);
        params.set("all_users", "true");
        if (projectFilter !== "all") params.set("project_id", projectFilter);
        return `${API}/expenses/export.csv?${params.toString()}`;
    };

    const [confirmExport, setConfirmExport] = useState(false);
    const [exporting, setExporting] = useState(false);

    const doExportPending = async () => {
        setExporting(true);
        try {
            const res = await http.get("/expenses/export/pending.xlsx", {
                params: { date_from: dateFrom, date_to: dateTo, mark_paid: true },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `dietas_pendientes_${dateFrom}_${dateTo}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Excel exportado. Los gastos incluidos se han marcado como pagados.");
            setConfirmExport(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo exportar el Excel");
        } finally {
            setExporting(false);
        }
    };

    const accessors = useMemo(
        () => ({
            date: (e) => e.expense_date || "",
            user: (e) => e.user_name || "",
            project: (e) => e.project_name || "",
            category: (e) => e.category || "",
            km: (e) => Number(e.km || 0),
            description: (e) => e.description || "",
            amount: (e) => Number(e.amount || 0),
            paid: (e) => (e.paid ? 1 : 0),
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        filtered,
        "admin-expenses",
        accessors,
        { initialKey: "date", initialDir: "desc" },
    );

    const catEntries = Object.entries(summary.byCat).sort((a, b) => b[1] - a[1]);
    const activeProjects = useMemo(
        () => projects.filter((p) => p.active !== false),
        [projects],
    );
    const sortedUsers = useMemo(
        () => [...users].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [users],
    );

    return (
        <div data-testid="page-admin-expenses">
            <PageHeader
                eyebrow="Administración"
                title="Gastos y dietas"
                description="Todos los gastos y dietas registrados por el equipo. Filtra por periodo, empleado, proyecto o categoría."
                actions={
                    <div className="flex items-center gap-2">
                        {drive.connected ? (
                            <span
                                className="text-xs flex items-center gap-1.5 px-3 py-2 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm"
                                title={
                                    drive.connected_email
                                        ? `Conectado como ${drive.connected_email}`
                                        : "Google Drive conectado"
                                }
                                data-testid="drive-connected-badge"
                            >
                                <CloudCheck size={15} weight="fill" /> Drive conectado
                            </span>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={connectDrive}
                                className="rounded-sm border-slate-300"
                                data-testid="drive-connect-btn"
                            >
                                <GoogleLogo size={15} className="mr-1.5" /> Conectar Google
                                Drive
                            </Button>
                        )}
                        <button
                            type="button"
                            onClick={() => setConfirmExport(true)}
                            className="text-sm flex items-center gap-1 px-3 py-2 border border-[#002BAF] bg-[#002BAF] text-white hover:bg-[#001F82] rounded-sm"
                            data-testid="admin-expenses-pending-xlsx"
                        >
                            <DownloadSimple size={14} /> Exportar pendientes (Excel)
                        </button>
                        <a
                            href={csvUrl()}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm flex items-center gap-1 px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm text-slate-700"
                            data-testid="admin-expenses-csv"
                        >
                            <DownloadSimple size={14} /> Exportar CSV
                        </a>
                    </div>
                }
            />
            <div className="p-6 lg:p-10 space-y-6">
                {/* Filters */}
                <div className="border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mr-2">
                        Filtros
                    </span>
                    <div>
                        <Label className="text-xs">Desde</Label>
                        <Input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="mt-1 rounded-sm border-slate-300 font-mono h-8"
                            data-testid="admin-expense-from"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Hasta</Label>
                        <Input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="mt-1 rounded-sm border-slate-300 font-mono h-8"
                            data-testid="admin-expense-to"
                        />
                    </div>
                    <div className="min-w-[200px]">
                        <Label className="text-xs">Empleado</Label>
                        <Select value={userFilter} onValueChange={setUserFilter}>
                            <SelectTrigger
                                className="mt-1 rounded-sm border-slate-300 h-8"
                                data-testid="admin-expense-user"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {sortedUsers.map((u) => (
                                    <SelectItem key={u.user_id} value={u.user_id}>
                                        {u.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="min-w-[220px]">
                        <Label className="text-xs">Proyecto</Label>
                        <SearchableSelect
                            value={projectFilter === "all" ? "" : projectFilter}
                            onChange={(v) => setProjectFilter(v || "all")}
                            options={activeProjects.map((p) => ({
                                value: p.project_id,
                                label: p.name,
                                hint: p.project_code || "",
                            }))}
                            placeholder="Todos"
                            className="mt-1 h-8 !py-1.5"
                            data-testid="admin-expense-project"
                        />
                    </div>
                    <div className="min-w-[170px]">
                        <Label className="text-xs">Categoría</Label>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger
                                className="mt-1 rounded-sm border-slate-300 h-8"
                                data-testid="admin-expense-category"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas</SelectItem>
                                {CATEGORIES.map((c) => (
                                    <SelectItem key={c.v} value={c.v}>
                                        {c.l}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                    <div className="bg-white p-6">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Total periodo
                        </div>
                        <div
                            className="font-mono text-3xl font-bold mt-2 text-slate-950"
                            data-testid="admin-expense-total"
                        >
                            {eur(summary.total)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            {summary.count} gasto{summary.count === 1 ? "" : "s"}
                            {summary.km > 0 && (
                                <>
                                    {" · "}
                                    {summary.km.toLocaleString("es-ES", {
                                        maximumFractionDigits: 1,
                                    })}{" "}
                                    km
                                </>
                            )}
                        </div>
                    </div>
                    <div className="bg-white p-6 lg:col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                            Por categoría
                        </div>
                        <div className="space-y-2">
                            {catEntries.length === 0 && (
                                <div className="text-sm text-slate-400">Sin datos.</div>
                            )}
                            {catEntries.map(([k, v]) => {
                                const cat = CAT_MAP[k];
                                const pct = summary.total > 0 ? (v / summary.total) * 100 : 0;
                                const Icon = cat?.Icon || DotsThreeCircle;
                                return (
                                    <div key={k} className="flex items-center gap-3 text-sm">
                                        <Icon size={14} className="text-slate-500 shrink-0" />
                                        <span className="w-40 truncate text-slate-700">
                                            {cat?.l || k}
                                        </span>
                                        <div className="flex-1 h-1.5 bg-slate-100 relative">
                                            <div
                                                className="absolute inset-y-0 left-0 bg-[#002BAF]"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                        <span className="font-mono tabular-nums text-slate-900 font-semibold w-24 text-right">
                                            {eur(v)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-expenses-table">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("date", "py-3 px-4 text-left")}>
                                    Fecha
                                    <SortIndicator active={sortKey === "date"} dir={sortDir} />
                                </th>
                                <th {...headerProps("user", "py-3 px-4 text-left")}>
                                    Empleado
                                    <SortIndicator active={sortKey === "user"} dir={sortDir} />
                                </th>
                                <th {...headerProps("project", "py-3 px-4 text-left")}>
                                    Proyecto
                                    <SortIndicator active={sortKey === "project"} dir={sortDir} />
                                </th>
                                <th {...headerProps("category", "py-3 px-4 text-left")}>
                                    Categoría
                                    <SortIndicator
                                        active={sortKey === "category"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("km", "py-3 px-4 text-right")}>
                                    Km
                                    <SortIndicator active={sortKey === "km"} dir={sortDir} />
                                </th>
                                <th {...headerProps("description", "py-3 px-4 text-left")}>
                                    Descripción
                                    <SortIndicator
                                        active={sortKey === "description"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("amount", "py-3 px-4 text-right")}>
                                    Importe
                                    <SortIndicator active={sortKey === "amount"} dir={sortDir} />
                                </th>
                                <th {...headerProps("paid", "py-3 px-4 text-center")}>
                                    Estado
                                    <SortIndicator active={sortKey === "paid"} dir={sortDir} />
                                </th>
                                <th className="py-3 px-4 text-center">Justificante</th>
                            </tr>
                        </thead>
                        <tbody>
                            {busy && (
                                <tr>
                                    <td colSpan={9} className="text-center py-12 text-slate-500">
                                        Cargando…
                                    </td>
                                </tr>
                            )}
                            {!busy && sorted.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="text-center py-12 text-slate-500">
                                        <Receipt
                                            size={28}
                                            className="mx-auto text-slate-300 mb-3"
                                        />
                                        No hay gastos registrados con estos filtros.
                                    </td>
                                </tr>
                            )}
                            {!busy &&
                                sorted.map((e) => {
                                    const cat = CAT_MAP[e.category];
                                    const Icon = cat?.Icon || DotsThreeCircle;
                                    return (
                                        <tr
                                            key={e.expense_id}
                                            className="border-b border-slate-100 hover:bg-slate-50"
                                            data-testid={`admin-expense-row-${e.expense_id}`}
                                        >
                                            <td className="py-3 px-4 font-mono text-slate-700">
                                                {e.expense_date}
                                            </td>
                                            <td className="py-3 px-4 text-slate-800 font-medium">
                                                {e.user_name}
                                            </td>
                                            <td className="py-3 px-4 text-slate-800">
                                                <div className="truncate max-w-xs">
                                                    {e.project_name}
                                                </div>
                                                {e.project_code && (
                                                    <div className="text-[11px] text-slate-400 font-mono">
                                                        {e.project_code}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                                                    <Icon size={12} />
                                                    {cat?.l || e.category}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-700">
                                                {e.km != null && e.km !== ""
                                                    ? Number(e.km).toLocaleString("es-ES", {
                                                          maximumFractionDigits: 2,
                                                      })
                                                    : "—"}
                                            </td>
                                            <td className="py-3 px-4 text-slate-600 max-w-sm truncate">
                                                {e.description || "—"}
                                            </td>
                                            <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-900 font-semibold">
                                                {eur(e.amount)}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <button
                                                    onClick={() => togglePaid(e)}
                                                    title={
                                                        e.paid
                                                            ? "Pagado — clic para marcar pendiente"
                                                            : "Pendiente — clic para marcar pagado"
                                                    }
                                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border transition-colors ${
                                                        e.paid
                                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                                            : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                                                    }`}
                                                    data-testid={`admin-expense-paid-${e.expense_id}`}
                                                >
                                                    {e.paid ? (
                                                        <CheckCircle size={14} weight="fill" />
                                                    ) : (
                                                        <Circle size={14} />
                                                    )}
                                                    {e.paid ? "Pagado" : "Pendiente"}
                                                </button>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <ReceiptButton
                                                    expense={e}
                                                    onUpdated={(u) =>
                                                        setItems((prev) =>
                                                            prev.map((it) =>
                                                                it.expense_id === u.expense_id
                                                                    ? u
                                                                    : it,
                                                            ),
                                                        )
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>

            <AlertDialog open={confirmExport} onOpenChange={setConfirmExport}>
                <AlertDialogContent data-testid="export-pending-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Exportar dietas pendientes</AlertDialogTitle>
                        <AlertDialogDescription>
                            ¿Deseas exportar el Excel de las dietas y gastos pendientes del periodo
                            seleccionado?
                            <br />
                            <span className="font-semibold text-amber-700">
                                Al exportar, todos los gastos incluidos se marcarán automáticamente
                                como PAGADOS.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            disabled={exporting}
                            data-testid="export-pending-cancel"
                        >
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(ev) => {
                                ev.preventDefault();
                                doExportPending();
                            }}
                            disabled={exporting}
                            className="bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="export-pending-confirm"
                        >
                            {exporting ? "Exportando…" : "Exportar y marcar pagadas"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
